import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

const COOKIE_CONSENT_KEY = 'sergio-cookie-consent'

export const CookieConsent: React.FC = () => {
  const [showConsent, setShowConsent] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      setShowConsent(true)
      // Delay to trigger slide-in animation
      setTimeout(() => setIsVisible(true), 100)
    }
  }, [])

  const acceptCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setIsVisible(false)
    setTimeout(() => setShowConsent(false), 200) // Wait for animation
    window.dispatchEvent(new CustomEvent('sergio-consent-granted'))
  }

  const declineCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    // Clear any existing cookies/local storage except essential ones
    const essentialKeys = [COOKIE_CONSENT_KEY]
    Object.keys(localStorage).forEach(key => {
      if (!essentialKeys.includes(key)) {
        localStorage.removeItem(key)
      }
    })
    setIsVisible(false)
    setTimeout(() => setShowConsent(false), 200) // Wait for animation
  }

  const closeToast = () => {
    setIsVisible(false)
    setTimeout(() => setShowConsent(false), 200)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'dismissed')
  }

  if (!showConsent) {
    return null
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9999] w-full max-w-[360px] transition-all duration-300 ease-out ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
    >
      <div className="bg-white dark:bg-[#1C1C1E] rounded-xl shadow-2xl border border-gray-200 dark:border-white/10 p-4">
        {/* Close button */}
        <button
          onClick={closeToast}
          className="absolute top-3 right-3 text-gray-800 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="pr-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Improve Your Experience
          </h3>
          <p className="text-sm text-gray-800 dark:text-gray-400 mb-3 leading-relaxed">
            We can remember your preferences locally to improve your experience.{' '}
            <a
              href="/privacy#cookies"
              className="text-[#0088DD] hover:underline"
            >
              Learn more
            </a>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={acceptCookies}
            className="flex-1 bg-[#0088DD] hover:bg-[#0077CC] text-white font-medium text-sm py-2 px-3 rounded-lg transition-colors"
          >
            Allow Preferences
          </button>
          <button
            onClick={declineCookies}
            className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 text-gray-800 dark:text-gray-300 font-medium text-sm py-2 px-3 rounded-lg transition-colors"
          >
            Continue Without
          </button>
        </div>
      </div>
    </div>
  )
}

// Hook to check if user has given consent
export const useCookieConsent = () => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null)

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    setHasConsent(consent === 'accepted')
  }, [])

  return hasConsent
}

// Utility to check if cookies are accepted (for use in other components)
export const isCookieConsentAccepted = (): boolean => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted'
}

// Utility to clear cookie consent (for testing or reset purposes)
export const clearCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY)
}
